import { Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { toast } from 'sonner';
import { useActionsPret, useParametresPrets } from '@/api/hooks';
import { DEVISES_PRET, LIBELLE_NATURE_PRET, NATURES_PRET, type DevisePret, type EngagementSaisie, type NaturePret } from '@/api/types';
import { useSession } from '@/auth/session';
import { Chargement, ErreurBloc, PageHeader } from '@/components/common';
import { montantDevise } from '@/components/rh';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SelectNatif } from '@/components/ui/select-natif';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';

type Ligne = { source: 'BANQUE' | 'FONAREV'; banque: string; montant: string; devise: DevisePret; mensualite: string; maturiteMois: string; dateDecaissement: string; soldeRestantDu: string };
const ligneVide = (devise: DevisePret): Ligne => ({ source: 'BANQUE', banque: '', montant: '', devise, mensualite: '', maturiteMois: '', dateDecaissement: '', soldeRestantDu: '' });

/** Étape 1 de la demande de prêt (§ 5.2 et 5.3) : montant souhaité, devise, nature, engagements existants ; transmission aux RH pour vérification du plafond. */
export function PretNouvelle() {
  const navigate = useNavigate();
  const u = useSession()!.utilisateur;
  const params = useParametresPrets();
  const actions = useActionsPret();
  const [nature, setNature] = useState<NaturePret>('CONSOMMATION');
  const [devise, setDevise] = useState<DevisePret>('USD');
  const [montant, setMontant] = useState('');
  const [objet, setObjet] = useState('');
  const [lignes, setLignes] = useState<Ligne[]>([]);
  if (params.isLoading) return <Chargement />;
  if (params.error || !params.data) return <ErreurBloc erreur={params.error} />;
  const p = params.data;
  const maj = (i: number, patch: Partial<Ligne>) => setLignes(lignes.map((l, k) => (k === i ? { ...l, ...patch } : l)));
  const engagements = (): EngagementSaisie[] =>
    lignes.map((l) => ({ source: l.source, banque: l.source === 'BANQUE' ? l.banque : null, montant: Number(l.montant), devise: l.devise, mensualite: Number(l.mensualite), maturiteMois: Number(l.maturiteMois), dateDecaissement: l.dateDecaissement || null, soldeRestantDu: Number(l.soldeRestantDu) }));
  const valide = Number(montant) > 0 && lignes.every((l) => Number(l.montant) >= 0 && Number(l.mensualite) >= 0 && Number(l.maturiteMois) >= 1 && Number(l.soldeRestantDu) >= 0 && (l.source !== 'BANQUE' || l.banque.trim()));
  const transmettre = async () => {
    try {
      const d = await actions.demander.mutateAsync({ nature, devise, montantSouhaite: Number(montant), objet: objet.trim() || null, engagements: engagements() });
      toast.success(`Demande ${d.demande.numero} transmise aux Ressources humaines pour vérification du plafond.`);
      navigate(`/rh/prets/${d.demande.id}`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Transmission impossible.');
    }
  };
  return (
    <div>
      <PageHeader
        titre="Demande de prêt social"
        sousTitre={`${u.prenom} ${u.nom} · ${u.fonction} · ${u.structureCode}`}
        retour={
          <Link to="/rh/prets" className="text-sm text-muted-foreground hover:underline">
            ← Mes prêts sociaux
          </Link>
        }
        actions={
          <Button onClick={transmettre} disabled={!valide || !p.eligibilite.eligible || actions.demander.isPending}>
            Transmettre aux Ressources humaines
          </Button>
        }
      />
      {!p.eligibilite.eligible && (
        <Card className="mb-4 border-amber-300">
          <CardHeader>
            <CardTitle className="text-base">Demande impossible</CardTitle>
            <CardDescription>{p.eligibilite.motif}</CardDescription>
          </CardHeader>
        </Card>
      )}
      <div className="grid gap-4 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle>Nature du prêt, montant et devise</CardTitle>
              <CardDescription>Première étape : vous indiquez seulement le montant souhaité. Les Ressources humaines calculent votre décompte final et vous communiquent le plafond ; la durée et la mensualité se fixent ensuite par simulation.</CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="nature">Nature du prêt</Label>
                <SelectNatif id="nature" value={nature} onChange={(e) => setNature(e.target.value as NaturePret)}>
                  {NATURES_PRET.map((n) => (
                    <option key={n} value={n}>
                      {LIBELLE_NATURE_PRET[n]} · {p.tauxAnnuel[n]} % l’an
                    </option>
                  ))}
                </SelectNatif>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="devise">Devise</Label>
                <SelectNatif id="devise" value={devise} onChange={(e) => setDevise(e.target.value as DevisePret)}>
                  {DEVISES_PRET.map((d) => (
                    <option key={d} value={d} disabled={d === 'CDF' && !p.tauxCdfUsd}>
                      {d === 'USD' ? 'Dollars américains (USD)' : `Francs congolais (CDF)${p.tauxCdfUsd ? '' : ' · taux de référence non renseigné'}`}
                    </option>
                  ))}
                </SelectNatif>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="montant">Montant souhaité</Label>
                <Input id="montant" type="number" min={1} step="0.01" value={montant} onChange={(e) => setMontant(e.target.value)} />
                <p className="text-xs text-muted-foreground">Le plafond communiqué s'entend du montant total à rembourser, intérêts compris ; durée au plus {p.dureeMaxMois} mois.</p>
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label htmlFor="objet">Objet (facultatif)</Label>
                <Textarea id="objet" rows={2} value={objet} onChange={(e) => setObjet(e.target.value)} />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle className="flex flex-wrap items-center justify-between gap-2">
                Prêts en cours (engagements existants)
                <Button size="sm" variant="outline" onClick={() => setLignes([...lignes, ligneVide(devise)])}>
                  <Plus className="size-4" /> Ajouter un engagement
                </Button>
              </CardTitle>
              <CardDescription>Déclarez vos prêts bancaires et FONAREV en cours (montant, mensualité, maturité, décaissement, solde restant dû) : ils entrent dans le calcul de la capacité mobilisée et de la capacité de remboursement ; les Ressources humaines les vérifient.</CardDescription>
            </CardHeader>
            <CardContent>
              {lignes.length === 0 ? (
                <p className="text-sm text-muted-foreground">Aucun prêt en cours déclaré.</p>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Source</TableHead>
                        <TableHead>Banque</TableHead>
                        <TableHead>Montant</TableHead>
                        <TableHead>Devise</TableHead>
                        <TableHead>Mensualité</TableHead>
                        <TableHead>Maturité (mois)</TableHead>
                        <TableHead>Décaissement</TableHead>
                        <TableHead>Solde restant dû</TableHead>
                        <TableHead />
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {lignes.map((l, i) => (
                        <TableRow key={i}>
                          <TableCell>
                            <SelectNatif value={l.source} onChange={(e) => maj(i, { source: e.target.value as Ligne['source'] })}>
                              <option value="BANQUE">Banque</option>
                              <option value="FONAREV">FONAREV</option>
                            </SelectNatif>
                          </TableCell>
                          <TableCell>
                            <Input value={l.banque} disabled={l.source !== 'BANQUE'} placeholder="Banque prêteuse" onChange={(e) => maj(i, { banque: e.target.value })} />
                          </TableCell>
                          <TableCell>
                            <Input type="number" min={0} step="0.01" className="w-28" value={l.montant} onChange={(e) => maj(i, { montant: e.target.value })} />
                          </TableCell>
                          <TableCell>
                            <SelectNatif value={l.devise} onChange={(e) => maj(i, { devise: e.target.value as DevisePret })}>
                              <option value="USD">USD</option>
                              <option value="CDF">CDF</option>
                            </SelectNatif>
                          </TableCell>
                          <TableCell>
                            <Input type="number" min={0} step="0.01" className="w-28" value={l.mensualite} onChange={(e) => maj(i, { mensualite: e.target.value })} />
                          </TableCell>
                          <TableCell>
                            <Input type="number" min={1} className="w-20" value={l.maturiteMois} onChange={(e) => maj(i, { maturiteMois: e.target.value })} />
                          </TableCell>
                          <TableCell>
                            <Input type="date" value={l.dateDecaissement} onChange={(e) => maj(i, { dateDecaissement: e.target.value })} />
                          </TableCell>
                          <TableCell>
                            <Input type="number" min={0} step="0.01" className="w-28" value={l.soldeRestantDu} onChange={(e) => maj(i, { soldeRestantDu: e.target.value })} />
                          </TableCell>
                          <TableCell>
                            <Button variant="ghost" size="icon" aria-label="Retirer" onClick={() => setLignes(lignes.filter((_, k) => k !== i))}>
                              <Trash2 className="size-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Repères</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm text-muted-foreground">
              <p>Ancienneté minimale : {p.ancienneteMinimaleMois} mois de service.</p>
              <p>Plafond : {p.quotiteDefaut} % du décompte final estimé ({p.quotiteMax} % au plus avec avis motivé), moins les soldes restants dus de vos prêts en cours ; valable {p.validitePlafondJours} jours.</p>
              <p>Capacité de remboursement : mensualités maintenues et nouvelle mensualité limitées à {p.seuilCapacite} % du salaire net (quotité du Code du travail) ; au-delà, l'avis des RH est défavorable et le Directeur Général décide.</p>
              <p>Taux : {p.tauxAnnuel.CONSOMMATION} % l'an (consommation), {p.tauxAnnuel.INVESTISSEMENT} % (investissement) ; aucun frais de dossier ; le prêt se rembourse un mois après son décaissement.</p>
              {montant && Number(montant) > 0 && <p className="text-foreground">Montant souhaité : {montantDevise(Number(montant), devise)}.</p>}
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle>Circuit</CardTitle>
              <CardDescription>Ressources humaines et Juridique → Direction financière (analyse de la Division des investissements) → DGA Administration et Finances → Directeur Général. La communication du plafond n'est pas une décision d'octroi.</CardDescription>
            </CardHeader>
          </Card>
        </div>
      </div>
    </div>
  );
}
